/**
 * Mobile product-card design presets, selected per form via `form.config.styles.mobileTheme`
 * (a sibling of `form.config.styles.colors`).
 *
 * Each preset is a bundle of CSS custom properties consumed by the
 * `@container (max-width: 630px)` rules in `product-selection.component.scss`. They are written
 * onto `document.documentElement` by the same `setColors()` pipeline that applies
 * `default_css.json` and the form's own colour overrides, so a preset behaves exactly like any
 * other themed value.
 *
 * Precedence is defaults -> mobileTheme preset -> the form's explicit `styles.colors` entries,
 * which means an operator can still override a single property of a preset by naming it in
 * `styles.colors` without having to abandon the preset.
 *
 * Themes:
 * - `default` — no preset applied; `default_css.json` supplies the compact look.
 * - `small`   — pins that compact look explicitly, so a form that opted in keeps it even if
 *               the shipped default changes later.
 * - `large`   — the image fills the card width at its natural aspect ratio (rather than being
 *               capped to a 70px height, which shrinks a 16:9 source to roughly 124x70 and
 *               leaves the card mostly empty) and gains rounded corners.
 *
 * An unrecognised value is ignored and falls back to the default look, so a future preset
 * added portal-side cannot break an older widget build.
 */
export const MOBILE_THEMES: { [theme: string]: { [cssVariable: string]: string } } = {
  small: {
    'wb-product-image-pane-mobile-margin': '16px',
    'wb-product-image-mobile-width': 'auto',
    'wb-product-image-mobile-height': '70px',
    'wb-product-image-mobile-max-height': '108px',
    'wb-product-image-mobile-min-height': '70px',
    'wb-product-image-mobile-radius': '0',
  },
  large: {
    'wb-product-image-pane-mobile-margin': '12px 12px 0 12px',
    'wb-product-image-mobile-width': '100%',
    'wb-product-image-mobile-height': 'auto',
    'wb-product-image-mobile-max-height': 'none',
    'wb-product-image-mobile-min-height': '0',
    'wb-product-image-mobile-radius': '8px',
  },
};

/**
 * Writes the custom properties for `mobileTheme` onto the document root.
 *
 * Call it between the `default_css.json` pass and the `styles.colors` pass of `setColors()` so
 * the precedence described above holds. Shared by `AppComponent` (standalone app) and
 * `InlineWebbookerComponent` (all embedded variants), which maintain separate `setColors()`
 * implementations — keeping the preset table here stops the two from drifting apart.
 */
export function applyMobileTheme(document: Document, mobileTheme?: string): void {
  const preset = mobileTheme ? MOBILE_THEMES[mobileTheme] : null;
  if (!preset) {
    return;
  }
  Object.keys(preset).forEach((cssVariable) => {
    document.documentElement.style.setProperty(`--${cssVariable}`, preset[cssVariable]);
  });
}
